import {PolymerElement, html} from '@polymer/polymer';
import {afterNextRender} from '@polymer/polymer/lib/utils/render-status';
import {ScopeBinding} from '../behaviors/keli-binding-behavior.js';
import {loadDynamicDependency} from '../../javascripts/modules/loadDynamicDependency.js';
import {TooltipBehavior} from '../behaviors/tooltip-behavior';
import {ResizeObserverBehavior} from '../behaviors/resize-observer-behavior.js';

class KeliChartFlamegraph extends ScopeBinding(TooltipBehavior(ResizeObserverBehavior(PolymerElement))) {
  static get template() {
    return html`
<style>
:host {
  display: block;
}

.d3-flame-graph rect {
  stroke: #EEEEEE;
  fill-opacity: .8;
}

.d3-flame-graph rect:hover {
  stroke: #474747;
  stroke-width: 0.5;
  cursor: pointer;
}

.d3-flame-graph-label {
  pointer-events: none;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  font-size: 12px;
  margin-left: 4px;
  margin-right: 4px;
  line-height: 1.5;
  padding: 0 0 0;
  font-weight: 400;
  color: black;
  text-align: left;
}

.d3-flame-graph .fade {
  opacity: 0.6 !important;
}

.d3-flame-graph .title {
  font-size: 20px;
}
</style>
<keli-chart-controller id="controller" keliname="_t"></keli-chart-controller>
<div id="chartContainer"></div>`
  }

  static get is() {
    return 'keli-chart-flamegraph';
  }

  static get properties() {
    return {
      chartData: {type: Object, observer: 'chartDataChanged'},
      colorMapper: {type: Object, scopevar: true, observer: 'colorMapperChanged'},
      flame: {type: Object},
      keliControlTypes: {type: Array, value: ['flamegraph']},
      labelHandler: {type: Object, scopevar: true, observer: 'labelHandlerChanged'},
      minFrameSize: {type: Number, scopevar: true, observer: 'minFrameSizeChanged'},
      needsDataLoaded: {type: Boolean, value: true},
      search: {type: String, scopevar: true, observer: 'searchChanged'},
      searchHandler: {type: Object, scopevar: true, observer: 'searchHandlerChanged'},
      searchMatch: {type: Object, scopevar: true, observer: 'searchMatchChanged'}
    }
  }

  constructor() {
    super();
    this.addEmitToKeli('keliChartElementActivated');
  }

  async connectedCallback() {
    await super.connectedCallback();

    await loadDynamicDependency([
      {global: window.d3, path: '/javascripts/d3/d3.min.js'},
      {global: window.flamegraph, path: '/javascripts/d3/d3-flamegraph.min.js'}
    ]);

    const tooltip = this.addTooltip(this.$.chartContainer, null, {
      followCursor: true,
      hideOnClick: false,
      placement: 'top',
      allowHTML: true
    });

    // Create a fake tooltip for the D3 Flamegraph to handle
    const fakeTooltip = new Function();
    fakeTooltip.show = () => null;
    fakeTooltip.hide = () => null;

    this.flame = window.flamegraph()
        .cellHeight(18)
        .transitionDuration(0)
        .transitionEase(() => 0)
        .minFrameSize(this.minFrameSize || 0)
        .sort(false)
        .inverted(true)
        .selfValue(true)
        .tooltip(fakeTooltip)
        .setDetailsHandler(data => {
          tooltip.setContent(data);
        })
        .onClick(row => {
          this.dispatchEvent(new CustomEvent('keliChartElementActivated', {detail: {data: row && row.data}}));
        });

    if (this.colorMapper) {
      this.flame.setColorMapper(this.colorMapper);
    }
    if (this.labelHandler) {
      this.flame.setLabelHandler(this.labelHandler);
    }
    if (this.searchHandler) {
      this.flame.setSearchHandler(this.searchHandler);
    }
    if (this.searchMatch) {
      this.flame.setSearchMatch(this.searchMatch);
    }

    afterNextRender(this, () => {
      if (this.$.chartContainer.offsetWidth === 0) {
        afterNextRender(this.$.chartContainer, () => {
          this.flame.width(this.$.chartContainer.offsetWidth);
          this.refresh();
        });
      } else {
        this.flame.width(this.$.chartContainer.offsetWidth);
        this.refresh();
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.flame) {
      this.flame.destroy();
    }
    this.flame = null;
    this.labelHandler = null;
    this.colorMapper = null;
  }

  async keliReady(scope) {
    super.keliReady && await super.keliReady(scope);
    await this.$.controller.elementReady(this);
  }

  refresh() {
    if (this.chartData) {
      this.chartDataChanged(this.chartData);
    }
  }

  chartDataChanged(newValue) {
    if (!this.flame) {
      return;
    }

    // First time loads need to initialize everything.
    if (!this.$.chartContainer.childElementCount) {
      window.d3.select(this.$.chartContainer).datum(newValue || []).call(this.flame);
      return;
    }

    this.flame.height(null);
    this.flame.update(newValue || []);
  }

  colorMapperChanged(newValue) {
    if (!this.flame) {
      return;
    }
    this.flame.setColorMapper(newValue);
  }

  labelHandlerChanged(newValue) {
    if (!this.flame) {
      return;
    }
    this.flame.setLabelHandler(newValue);
  }

  minFrameSizeChanged(newValue) {
    if (!this.flame) {
      return;
    }
    this.flame.minFrameSize(newValue);
    this.refresh();
  }

  searchChanged(newValue) {
    if (!this.flame) {
      return;
    }
    this.flame.search(newValue);
  }

  searchHandlerChanged(newValue) {
    if (!this.flame) {
      return;
    }
    this.flame.setSearchHandler(newValue);
  }

  searchMatchChanged(newValue) {
    if (!this.flame) {
      return;
    }
    this.flame.setSearchMatch(newValue);
  }

  handleElementResized() {
    if (!this.flame) {
      return;
    }

    const width = this.$.chartContainer.offsetWidth;
    const svg = this.$.chartContainer.querySelector('svg');
    if (svg) {
      svg.setAttribute('width', `${width}px`);
      this.flame.width(width);
      this.refresh();
    }
  }
}

customElements.define(KeliChartFlamegraph.is, KeliChartFlamegraph);
export {KeliChartFlamegraph};
